/**
 * The parts of a form that are not a box you type in.
 *
 * A switch with its consequence under it, a checkbox with its consequence
 * under it, a row of chips for a short multi-select, a colour picker, a photo
 * drop target and an address block. `docs/record-flyouts.md` §3.
 *
 * The rule they share: **the second line is what the setting does, not what
 * it is called.** "Track stock at the yards / on-hand, reserved and purchasing
 * follow this item" tells somebody who has never seen the inventory screen
 * what ticking it costs them. A switch with only a label is a switch you have
 * to try.
 */
import { type ReactNode } from 'react'
import { regionFieldLabel, type Country } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Checkbox, Switch } from '../primitives.js'
import { Field } from './field.js'
import { TextInput } from './inputs.js'
import { AddressAutocomplete } from './address-autocomplete.js'
import { RegionSelect } from './region-select.js'
import { MenuSelect } from '../menu-select.js'
import { IconCamera, IconCheck } from '../../frame/icons.js'

/**
 * One setting, one toggle — 13.5/600 label, 12px muted consequence, switch
 * right, a `line-faint` rule between rows and none under the last.
 *
 * A disabled row keeps its label and says why in the sub rather than going
 * grey and silent: "needs the Rentals feature" is the answer to the question
 * the greyed control provokes.
 */
export function SwitchRow({ label, sub, checked, onChange, disabled }: {
  label: string
  sub?: ReactNode
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className={cn('flex items-center gap-3 border-b border-line-faint py-2.5',
      'last:border-b-0', disabled && 'opacity-55')}>
      <span className="flex min-w-0 flex-col gap-px">
        <span className="text-[13.5px] font-semibold text-text">{label}</span>
        {sub && <span className="text-[12px] leading-[1.4] text-text-muted">{sub}</span>}
      </span>
      <span className="ml-auto shrink-0">
        <Switch checked={checked} onChange={onChange} label={label} hideLabel disabled={disabled} />
      </span>
    </div>
  )
}

/**
 * One independent option, ticked or not, with the same second line.
 *
 * The two lines go *inside* `Checkbox`'s own label rather than beside it. A
 * second `<label>` wrapped around this one would be invalid markup, and it
 * put the words in the DOM twice — once visible, once in the accessible name
 * — so a screen reader read "Taxable Taxable".
 */
export function CheckRow({ label, sub, checked, onChange, disabled }: {
  label: string
  sub?: ReactNode
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}) {
  return (
    <Checkbox
      align="start"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      className={cn('gap-2.5 py-1.5',
        disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer')}
      label={(
        <span className="flex min-w-0 flex-col gap-px">
          <span className={cn('text-[13.5px] text-text',
            checked ? 'font-semibold' : 'font-medium')}>
            {label}
          </span>
          {sub && <span className="text-[12px] leading-[1.4] text-text-muted">{sub}</span>}
        </span>
      )}
    />
  )
}

export interface ChipOption { value: string; label: string }

/**
 * A short multi-select drawn as pills rather than a list with checkboxes.
 *
 * For divisions, branches and roles: four to eight named things where seeing
 * all of them at once *is* the control. A dropdown would hide the answer
 * behind a click and a chips row would be silly for fifty, which is what the
 * `add` link is for — "+ Limit to a branch" opens the real picker.
 */
export function ChipsField({ options, value, onChange, add, label, labelledBy }: {
  options: ChipOption[]
  value: string[]
  onChange: (next: string[]) => void
  add?: { label: string; onClick: () => void }
  label: string
  /** A `Field`'s `labelId`, when the label above already names this group. */
  labelledBy?: string
}) {
  const toggle = (id: string) => onChange(
    value.includes(id) ? value.filter((one) => one !== id) : [...value, id])

  return (
    <div
      role="group"
      /* Named by the label above when there is one; naming it twice reads twice. */
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      className="flex flex-wrap items-center gap-2"
    >
      {options.map((option) => {
        const on = value.includes(option.value)
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={on}
            onClick={() => toggle(option.value)}
            className={cn('inline-flex h-7 items-center gap-1.5 rounded-chip border px-2.5',
              'text-[12.5px] font-semibold',
              on
                ? 'border-accent-line bg-accent-soft text-accent-dark'
                : 'border-line bg-surface text-text-body hover:bg-hover')}
          >
            {on && <IconCheck size={11} />}
            {option.label}
          </button>
        )
      })}
      {add && (
        <button
          type="button"
          onClick={add.onClick}
          className="text-[12.5px] font-semibold text-accent hover:text-accent-dark"
        >
          + {add.label}
        </button>
      )}
    </div>
  )
}

/**
 * A colour, chosen as a colour.
 *
 * The one place where naming the options in words is not a picker: a division
 * is "the green one" to everybody who uses the board, and a dropdown reading
 * "Maintenance green / Enhancements amber" is a list of names for things you
 * are looking straight at.
 */
export function SwatchField({ options, value, onChange, label, labelledBy, shape = 'square' }: {
  options: { value: string; css: string; label: string }[]
  value: string | null
  onChange: (value: string) => void
  label: string
  /** `Field`'s own label, when there is one above. See `ChipsField`. */
  labelledBy?: string
  /**
   * A zone colour is a square because it is a patch on a map; a mark colour is
   * a circle at 30px, because board 4 draws it that way beside the icon grid
   * and the two rows read as one decision rather than two kinds of control.
   */
  shape?: 'square' | 'circle'
}) {
  return (
    <div
      role="radiogroup"
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      className="flex flex-wrap items-center gap-2"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          aria-label={option.label}
          title={option.label}
          onClick={() => onChange(option.value)}
          style={{ background: option.css }}
          /*
            * Chosen = a white gap inside a dark ring, so the mark reads on a
            * dark swatch and a light one alike. The border is always there
            * and only changes colour, so picking one does not resize it.
            */
          className={cn(
            'border-2',
            shape === 'circle' ? 'h-[30px] w-[30px] rounded-full' : 'h-[26px] w-[26px] rounded-[8px]',
            option.value === value
              ? 'border-surface ring-2 ring-text'
              : 'border-transparent')}
        >
          {/* The chosen one carries a check, which is what says "picked" on a
              circle — a ring alone reads as a hover on a round swatch. */}
          {shape === 'circle' && option.value === value && (
            <IconCheck size={15} className="mx-auto text-white" />
          )}
        </button>
      ))}
    </div>
  )
}

/**
 * Somewhere to put a photo, which says what the photo is for.
 *
 * Dashed rather than solid, because a drop target that looks like a field
 * looks like a field you cannot type in.
 */
export function PhotoDrop({ onPick, sub, busy, preview }: {
  onPick: (file: File) => void
  sub?: ReactNode
  busy?: boolean
  /** A URL, once there is one — the target becomes a thumbnail with a Replace. */
  preview?: string | null
}) {
  return (
    <label className={cn('flex cursor-pointer items-center gap-3 rounded-[10px] border',
      'border-dashed border-line-strong bg-surface-alt p-3 hover:border-accent-line')}>
      <input
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) onPick(file)
          event.target.value = ''
        }}
      />
      {preview
        ? (
          <img
            src={preview}
            alt=""
            className="h-10 w-10 shrink-0 rounded-[10px] border border-line object-cover"
          />
        )
        : (
          <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center',
            'rounded-[10px] border border-line bg-surface text-text-muted')}>
            <IconCamera size={18} />
          </span>
        )}
      <span className="flex min-w-0 flex-col gap-px">
        <span className="text-[13px] font-semibold text-text">
          {busy
            ? 'Uploading…'
            : preview
              ? <>Photo added, or <span className="text-accent">choose another</span></>
              : <>Drop a photo, or <span className="text-accent">choose one</span></>}
        </span>
        {sub && <span className="text-fieldlabel leading-[1.4] text-text-faint">{sub}</span>}
      </span>
    </label>
  )
}

export interface AddressValue {
  line1: string
  line2: string
  city: string
  /** A two-letter region code — `shared/src/regions.ts` decides which. */
  state: string
  postalCode: string
  /**
   * Which country's list the state comes from, and which shape the postal
   * code takes. Left out means the US, which is every address in the product
   * until a company turns the international setting on.
   */
  country?: Country
  /**
   * Where it is, when a suggestion said so.
   *
   * Optional because most of this product's addresses are typed rather than
   * picked, and a property with no geocode is a property — plan §4.6 pairs
   * the geocode with the normalized string for the duplicate check, and one
   * of the two is enough to have. A form that does not care can ignore both.
   */
  latitude?: number | null
  longitude?: number | null
}

/**
 * One address, as one block.
 *
 * Street on its own row, then city wider than state and ZIP — the shape a
 * posted envelope has, so somebody copying from one does not have to hunt.
 * Five fields in five grid cells would put the ZIP under the street.
 */
export function AddressFields({
  value, onChange, suite = true, required, errors, suggest = true, international = false,
}: {
  value: AddressValue
  onChange: (next: AddressValue) => void
  /** The "Suite, unit" line, for a record that can have one. */
  suite?: boolean
  required?: boolean
  /**
   * What is wrong, by field — `{ city: 'Which town?' }`.
   *
   * Without this a form that marks the address required has no way to say
   * which line failed: the footer counts five fields needing attention and
   * none of them is marked. The keys are `AddressValue`'s, so a caller that
   * validates `postalCode` names it `postalCode` here too.
   */
  errors?: Partial<Record<keyof AddressValue, string>>
  /**
   * Offer Google's suggestions on the street line. On by default; off for a
   * form where the address is not a place anybody drives to.
   *
   * With no `GOOGLE_MAPS_API_KEY` on the server this changes nothing — the
   * field asks once, is told there is no key, and never opens a menu.
   */
  suggest?: boolean
  /**
   * Offer the country row. Off by default — see the comment on it below.
   *
   * A prop rather than a read of the company setting here, because this
   * component is in the field kit and the kit does not fetch.
   */
  international?: boolean
}) {
  const set = (part: Partial<AddressValue>) => onChange({ ...value, ...part })
  const country: Country = value.country ?? 'US'

  return (
    <div className="flex flex-col gap-3">
      <Field label="Address" required={required} error={errors?.line1}>
        {({ id, describedBy, invalid }) => (suggest
          ? (
            <AddressAutocomplete
              id={id}
              describedBy={describedBy}
              invalid={invalid}
              value={value.line1}
              placeholder="41 Grantley Rd"
              onChange={(line1) => set({ line1 })}
              /*
               * A whole address at once: the street line *and* the three
               * boxes under it, because retyping a town the menu just told
               * you is the part of this everybody hates.
               */
              onChoose={(found) => set(found)}
            />
          )
          : (
            <TextInput
              id={id}
              describedBy={describedBy}
              invalid={invalid}
              value={value.line1}
              placeholder="41 Grantley Rd"
              onChange={(event) => set({ line1: event.target.value })}
            />
          ))}
      </Field>

      {suite && (
        <Field label="Suite, unit" optional>
          {({ id }) => (
            <TextInput
              id={id}
              value={value.line2}
              onChange={(event) => set({ line2: event.target.value })}
            />
          )}
        </Field>
      )}

      <div className="grid grid-cols-[1.4fr_1fr] gap-x-3.5">
        <Field label="City" required={required} error={errors?.city}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              describedBy={describedBy}
              invalid={invalid}
              value={value.city}
              onChange={(event) => set({ city: event.target.value })}
            />
          )}
        </Field>
        <div className="grid grid-cols-2 gap-x-3.5">
          {/*
            * A select, never a text box (`address-state-field.md` §1). The
            * two-character box accepted "Pa" and "pa" and "PE" for
            * Pennsylvania, and a State column then sorted the same place
            * three ways.
            */}
          <Field
            label={regionFieldLabel(country)}
            required={required}
            error={errors?.state}
          >
            {({ id, describedBy, invalid }) => (
              <RegionSelect
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                country={country}
                value={value.state}
                onChange={(state) => set({ state })}
              />
            )}
          </Field>
          <Field
            label={country === 'CA' ? 'Postal code' : 'ZIP'}
            required={required}
            error={errors?.postalCode}
          >
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                value={value.postalCode}
                /*
                 * A Canadian postal code has letters in it, so the numeric
                 * keypad would be the wrong keyboard and `maxLength` the
                 * wrong length. One field, two shapes, decided by the row.
                 */
                inputMode={country === 'CA' ? 'text' : 'numeric'}
                maxLength={country === 'CA' ? 7 : 10}
                placeholder={country === 'CA' ? 'A1A 1A1' : undefined}
                onChange={(event) => set({ postalCode: event.target.value })}
              />
            )}
          </Field>
        </div>
      </div>

      {/*
        * Only when the company has an address outside the US (§1.2). One
        * country is not a choice, and a select offering it is a row of
        * furniture on every address form in the product.
        */}
      {international && (
        <div className="grid grid-cols-[1.4fr_1fr] gap-x-3.5">
          <Field label="Country">
            {({ id }) => (
              <MenuSelect
                size="field"
                id={id}
                label="Country"
                value={country}
                onChange={(next) => set({
                  country: (next ?? 'US') as Country,
                  // The state belonged to the old list, and PA is not a
                  // province. Clearing it is the honest move: leaving it
                  // would store a code this row's country cannot read.
                  ...(next && next !== country ? { state: '' } : {}),
                })}
                options={[
                  { value: 'US', label: 'US', sub: 'United States' },
                  { value: 'CA', label: 'CA', sub: 'Canada' },
                ]}
                minWidth={200}
              />
            )}
          </Field>
        </div>
      )}
    </div>
  )
}
